import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudScroll } from './ChineseDecor';
import ASSETS from '../assets/assets';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';

interface FooterProps {
  content?: Record<string, unknown>;
}

const Footer: React.FC<FooterProps> = ({ content: propContent }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const [cmsContent, setCmsContent] = useState<Record<string, unknown> | undefined>(propContent);

  // If no CMS content was provided (e.g. shop pages), fetch the footer section ourselves.
  useEffect(() => {
    if (propContent && Object.keys(propContent).length > 0) return;

    let cancelled = false;
    fetch('/api/public/cms/sections')
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const footer = data?.data?.find((s: { key: string }) => s.key === 'footer');
        if (footer?.contentJson) {
          setCmsContent(footer.contentJson);
        }
      })
      .catch((err) => {
        console.warn('Failed to load footer CMS content:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [propContent]);

  const content = cmsContent || {};

  const slogan = getLocalizedText(content.slogan, lang, t('footer.slogan'));
  const desc = getLocalizedText(content.desc, lang, t('footer.desc'));
  const brandSince = getLocalizedText(content.brandSince, lang, t('brand.since'));
  const address = getLocalizedText(content.address, lang, t('footer.address'));
  const phone = (content.phone as string | undefined) || '(852) 2350 9118';
  const whatsapp = (content.whatsapp as string | undefined) || '5120 8911 / 6093 8333';
  const rights = getLocalizedText(content.rights, lang, t('footer.rights'));
  const termsLabel = getLocalizedText(content.termsLabel, lang, t('footer.terms'));
  const privacyLabel = getLocalizedText(content.privacyLabel, lang, t('footer.privacy'));
  const shippingLabel = getLocalizedText(content.shippingLabel, lang, t('footer.shipping'));

  return (
    <footer className="relative pt-8 pb-24 md:pb-16 bg-brand-ink text-brand-paper/50 font-sans border-t border-brand-gold/20 overflow-hidden">
      <div className="absolute inset-0 bg-cloud opacity-[0.12] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Slogan */}
        <div className="text-center mb-12">
          <img
            src={ASSETS.logo}
            alt={t('brand.name')}
            className="h-14 w-auto mx-auto mb-4 object-contain"
          />
          <p className="text-brand-paper font-serif text-xl md:text-2xl tracking-[0.15em] mb-3">
            {slogan}
          </p>
          <p className="text-brand-paper/50 text-[13px] max-w-xl mx-auto leading-relaxed">
            {desc}
          </p>
          <div className="flex justify-center mt-6">
            <CloudScroll className="w-20 h-8 text-brand-gold/40" />
          </div>
        </div>

        {/* Brand + contact */}
        <div className="grid md:grid-cols-2 gap-8 items-start border-t border-brand-paper/10 pt-10 text-[13px]">
          <div>
            <p className="text-brand-paper/90 font-serif text-lg tracking-[0.2em]">
              {t('brand.name')} <span className="text-brand-gold text-xs tracking-[0.3em] ml-1">{t('brand.name_en')}</span>
            </p>
            <p className="mt-2 text-brand-paper/40">{brandSince}</p>
          </div>
          <div className="space-y-1.5">
            <p><span className="text-brand-gold">{t('footer.address_label')}：</span>{address}</p>
            <p><span className="text-brand-gold">{t('footer.phone')}：</span>{phone}</p>
            <p><span className="text-brand-gold">{t('footer.whatsapp_label')}：</span>{whatsapp}</p>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-brand-paper/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs tracking-widest">
          <p>© {new Date().getFullYear()} {t('brand.name_en')}. {rights}</p>
          <div className="flex space-x-6">
            <a href="#" className="hover:text-brand-gold transition-colors">{termsLabel}</a>
            <a href="#" className="hover:text-brand-gold transition-colors">{privacyLabel}</a>
            <a href="#" className="hover:text-brand-gold transition-colors">{shippingLabel}</a>
          </div>
        </div>

        <div className="mt-8 text-center text-[11px] text-brand-paper/30 tracking-wide">
          Powered by{' '}
          <a
            href="https://jkdcoding.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-gold/70 hover:text-brand-gold transition-colors"
          >
            JKDCoding
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
